import React, { useState, useEffect } from 'react';
import * as FormBuilderData from '../Form/BuilderTools/FormBuilderData';
import { Form } from '../Form/Form';

function FormComparison({ comparisonData, onSave, onCancel }) {
  const [systemForm, setSystemForm] = useState(null);
  const [existingForm, setExistingForm] = useState(null);
  const [mergedForm, setMergedForm] = useState(null);
  const [questionChoices, setQuestionChoices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadForms();
  }, [comparisonData]);

  async function loadForms() {
    if (!comparisonData) return;

    try {
      setLoading(true);
      setError(null);
      
      const sysForm = comparisonData.systemForm;
      const existForm = comparisonData.existingForm;
      
      if (!sysForm || !existForm) {
        throw new Error('Failed to load form data');
      }
      
      setSystemForm(sysForm);
      setExistingForm(existForm);
      
      // Initialize merged form with system form as base
      const merged = { ...sysForm };
      merged.ID = comparisonData.newFormId || existForm.ID;
      merged.Name = existForm.Name;
      setMergedForm(merged);
      
      // Initialize question choices (default to system form)
      const choices = {};
      sysForm.Pages?.forEach((page) => {
        page.Questions?.forEach((question) => {
          choices[question.ID] = 'system';
        });
      });
      setQuestionChoices(choices);
    } catch (err) {
      setError(err.message || 'Failed to load forms for comparison');
    } finally {
      setLoading(false);
    }
  }

  function handleQuestionChoice(questionId, choice) {
    setQuestionChoices(prev => ({
      ...prev,
      [questionId]: choice
    }));
  }

  function buildMergedForm() {
    if (!systemForm || !existingForm) return null;

    // Create a proper Form instance
    const merged = new Form(existingForm.Name, systemForm.Type);
    
    // Copy all properties from system form
    Object.assign(merged, systemForm);
    
    // Override with specific values
    merged.ID = comparisonData.newFormId || existingForm.ID;
    merged.Name = existingForm.Name;
    merged.DisableInheritance = true;
    
    // Apply question choices
    merged.Pages = systemForm.Pages?.map((page) => ({
      ...page,
      Questions: page.Questions?.reduce((questions, question) => {
        const choice = questionChoices[question.ID];
        
        // Find existing question by ID across all pages
        let existingQuestion = null;
        for (const existingPage of existingForm.Pages || []) {
          existingQuestion = existingPage.Questions?.find(q => q.ID === question.ID);
          if (existingQuestion) break;
        }
        
        if (choice === 'existing') {
          if (existingQuestion) questions.push(existingQuestion);
        } else if (choice === 'both') {
          questions.push(question);
          if (existingQuestion) questions.push(existingQuestion);
        } else if (choice === 'none') {
          // Skip this question entirely
        } else {
          questions.push(question);
        }
        
        return questions;
      }, [])
    }));
    
    return merged;
  }

  async function handleSave() {
    setSaving(true);
    try {
      const finalForm = buildMergedForm();
      if (finalForm) {
        await onSave(finalForm);
      }
    } catch (err) {
      setError('Failed to save merged form. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5>Loading form comparison...</h5>
        <p className="text-muted">Please wait while we prepare your forms for comparison.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Forms</h4>
          <p>{error}</p>
          <hr />
          <button className="btn btn-outline-danger" onClick={onCancel}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!systemForm || !existingForm) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Forms Not Available</h4>
          <p>Unable to load the forms for comparison.</p>
          <button className="btn btn-outline-warning" onClick={onCancel}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Form Comparison: {existingForm.Name}</h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="alert alert-info mb-0 p-2">
              <strong>Instructions:</strong> A form for this type already exists within your account. Select which questions you want to be included in your new version.
            </div>

            <div className="alert alert-info mb-3 p-2">
              <strong>Tip:</strong> Choose system version, your existing version, or include both per question. When both are used the system question will be placed before the existing question.
            </div>
            
            <div className="row mb-3 text-center">
              <div className="col-2">
                <strong className="text-primary">
                  {Object.values(questionChoices).filter(choice => choice === 'system').length}
                </strong><br />
                <small>System Only</small>
              </div>
              <div className="col-2">
                <strong className="text-success">
                  {Object.values(questionChoices).filter(choice => choice === 'existing').length}
                </strong><br />
                <small>Existing Only</small>
              </div>
              <div className="col-2">
                <strong className="text-warning">
                  {Object.values(questionChoices).filter(choice => choice === 'both').length}
                </strong><br />
                <small>Include Both</small>
              </div>
              <div className="col-2">
                <strong className="text-danger">
                  {Object.values(questionChoices).filter(choice => choice === 'none').length}
                </strong><br />
                <small>Excluded</small>
              </div>
              <div className="col-4">
                <strong className="text-muted">
                  {Object.keys(questionChoices).length}
                </strong><br />
                <small>Total Questions</small>
              </div>
            </div>

            <div className="comparison-container">
        {systemForm.Pages?.map((systemPage, pageIndex) => {
          const existingPage = existingForm.Pages?.[pageIndex];
          
          return (
            <div key={pageIndex} className="mb-4">
              <h6 className="bg-light p-2 rounded"><span dangerouslySetInnerHTML={{ __html: systemPage.Summary }} /></h6>
              
              {systemPage.Questions?.map((systemQuestion, questionIndex) => {
                // Find existing question by ID across all pages
                let existingQuestion = null;
                for (const existingPage of existingForm.Pages || []) {
                  existingQuestion = existingPage.Questions?.find(q => q.ID === systemQuestion.ID);
                  if (existingQuestion) break;
                }
                const currentChoice = questionChoices[systemQuestion.ID];
                
                return (
                  <div key={questionIndex} className="mb-3 border rounded p-3">
                    <div className="row">
                      <div className="col-5">
                        <div className={`p-2 rounded ${currentChoice === 'system' ? 'bg-primary bg-opacity-10 border-primary' : 'bg-light'}`}>
                          <strong className="text-primary">System:</strong> <span dangerouslySetInnerHTML={{ __html: systemQuestion.Text }} />
                          <div className="small text-muted">Type: {systemQuestion.Type}</div>
                        </div>
                      </div>
                      <div className="col-2 text-center">
                        <div className="btn-group-vertical" role="group">
                          <button
                            className={`btn btn-sm ${currentChoice === 'system' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handleQuestionChoice(systemQuestion.ID, 'system')}
                          >
                            System
                          </button>
                          {existingQuestion && (
                            <>
                              <button
                                className={`btn btn-sm ${currentChoice === 'existing' ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={() => handleQuestionChoice(systemQuestion.ID, 'existing')}
                              >
                                Existing
                              </button>
                              <button
                                className={`btn btn-sm ${currentChoice === 'both' ? 'btn-warning' : 'btn-outline-warning'}`}
                                onClick={() => handleQuestionChoice(systemQuestion.ID, 'both')}
                              >
                                Both
                              </button>
                            </>
                          )}
                          <button
                            className={`btn btn-sm ${currentChoice === 'none' ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => handleQuestionChoice(systemQuestion.ID, 'none')}
                          >
                            None
                          </button>
                        </div>
                      </div>
                      <div className="col-5">
                        {existingQuestion ? (
                          <div className={`p-2 rounded ${currentChoice === 'existing' ? 'bg-success bg-opacity-10 border-success' : 'bg-light'}`}>
                            <strong className="text-success">Existing:</strong> <span dangerouslySetInnerHTML={{ __html: existingQuestion.Text }} />
                            <div className="small text-muted">Type: {existingQuestion.Type}</div>
                          </div>
                        ) : (
                          <div className="p-2 bg-light rounded text-muted text-center">
                            <em>No existing question</em>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button 
              className="btn btn-success" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                'Save Merged Form'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormComparison;